import React, { Component } from "react";
import { message, Upload, Table, Tag, Checkbox, Alert, Typography, Divider, Result, Switch, Collapse, Popconfirm } from "antd";
import { UploadOutlined, FileExcelOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import axios from "axios";
import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import { ModalItem, ButtonItem } from "../../../../Components/Items";

const { Text } = Typography;

// Colapsa los saltos de línea y espacios repetidos que traen las celdas de
// algunos estados de cuenta (BBVA exporta "818.00 MXN\r\n\r\n", por ejemplo).
const limpiarTexto = (valor) => String(valor ?? "").replace(/\s+/g, " ").trim();

const normalizar = (valor) => limpiarTexto(valor).toUpperCase();

const formatMonto = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const parseMonto = (valor) => {
  if (valor === undefined || valor === null || valor === "") return 0;
  if (typeof valor === "number") return valor;
  // Se quita la divisa antes de descartar la celda por traer letras: BBVA
  // exporta el importe como texto ("-2,235.09 MXN"), no como número.
  const texto = limpiarTexto(valor)
    .replace(/\b(MXN|MXP|USD|EUR|M\.?N\.?)\b/gi, "")
    .replace(/\$/g, "")
    .trim();
  if (/[a-zA-Z]/.test(texto)) return 0; // sigue siendo una celda de texto
  const limpio = texto.replace(/[^0-9.-]/g, "");
  if (!/\d/.test(limpio)) return 0;
  const num = parseFloat(limpio);
  return Number.isNaN(num) ? 0 : num;
};

// Busca en la fila la columna cuyo encabezado contenga alguno de los textos dados.
const encontrarColumna = (filaEncabezado, textosBuscar) => {
  for (let i = 0; i < filaEncabezado.length; i++) {
    const celda = normalizar(filaEncabezado[i]);
    if (textosBuscar.some((t) => celda.includes(t))) return i;
  }
  return -1;
};

// Igual que encontrarColumna pero regresa todas las columnas que coincidan.
const encontrarColumnas = (filaEncabezado, textosBuscar) => {
  const indices = [];
  for (let i = 0; i < filaEncabezado.length; i++) {
    const celda = normalizar(filaEncabezado[i]);
    if (textosBuscar.some((t) => celda.includes(t))) indices.push(i);
  }
  return indices;
};

// La referencia son 6 caracteres buscados por substring dentro del concepto del
// movimiento, así que el texto fijo del banco ("CUENTA", "TERCERO", "TRANSF")
// puede casar por accidente. Cuando hay más de una posibilidad no se adivina:
// se marca como ambigua y la revisa el operador.
const buscarCoincidencias = (conceptoReferencia, ventasPendientes) => {
  const texto = normalizar(conceptoReferencia);
  return ventasPendientes.filter((v) => v.referencia && texto.includes(normalizar(v.referencia)));
};

// Segunda pasada: si una misma venta quedó emparejada con varios movimientos
// aplicables, ninguno de ellos es confiable.
const marcarVentasDuplicadas = (candidatos) => {
  const aplicables = (c) => c.estado === "exacto" || c.estado === "confirmar_monto";
  const conteoPorVenta = new Map();
  candidatos.forEach((c) => {
    if (!aplicables(c) || !c.venta) return;
    conteoPorVenta.set(c.venta._id, (conteoPorVenta.get(c.venta._id) || 0) + 1);
  });
  return candidatos.map((c) =>
    aplicables(c) && c.venta && conteoPorVenta.get(c.venta._id) > 1 ? { ...c, estado: "ambiguo" } : c,
  );
};

const COLOR_PAGADA_AHORA = "47FF50"; // verde
const COLOR_MONTO_PROBLEMA = "FFF154"; // amarillo
const COLOR_YA_PAGADA = "63DFFF"; // azul

class ModalConciliarTransferenciasExcel extends Component {
  state = {
    cargando: false,
    ventasPendientes: [],
    archivoNombre: null,
    candidatos: [], // filas Abono emparejadas con su clasificación
    seleccionadas: {}, // ventaId -> bool
    resultados: null, // null hasta aplicar
    cargosIgnorados: 0,
    mostrarNoCoincidencias: false,
    filasOriginales: [],
    formatoExcel: null, // "bancario" | "escolar"
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        archivoNombre: null,
        candidatos: [],
        seleccionadas: {},
        resultados: null,
        cargosIgnorados: 0,
        mostrarNoCoincidencias: false,
        filasOriginales: [],
        formatoExcel: null,
      });
      this.consultarPendientes();
    }
  }

  consultarPendientes = () => {
    const { empresaId, headersToken } = this.props;
    this.setState({ cargando: true });
    axios
      .post("ventasEcommerce/pendientesTransferenciaFija", { empresaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ ventasPendientes: res.data.data || [] });
        } else {
          message.error(res.data.message || "No se pudieron obtener las ventas pendientes");
        }
      })
      .catch(() => message.error("Error de conexión con el servidor"))
      .finally(() => this.setState({ cargando: false }));
  };

  onSubirArchivo = (info) => {
    const file = info.file;
    const tiposValidos = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!tiposValidos.includes(file.type) && !/\.xlsx?$/i.test(file.name || "")) {
      message.error("El archivo debe ser un Excel (.xlsx/.xls)");
      return;
    }
    this.setState({ cargando: true, archivoNombre: file.name });

    const reader = new FileReader();
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
        const filas = XLSX.utils.sheet_to_json(primeraHoja, { header: 1, defval: "" });
        this.procesarFilas(filas);
      } catch (err) {
        console.error(err);
        message.error("No se pudo leer el archivo. Verifica que sea un Excel válido.");
        this.setState({ cargando: false });
      }
    };
    reader.readAsArrayBuffer(file.originFileObj || file);
  };

  procesarFilas = (filas) => {
    const filaEncabezado = filas[0] || [];
    const esFormatoEscolar =
      encontrarColumna(filaEncabezado, ["MATRICULA", "MATRÍCULA"]) !== -1 &&
      encontrarColumna(filaEncabezado, ["REFERENCIA"]) !== -1;
    if (esFormatoEscolar) {
      this.procesarFilasEscolar(filas);
      return;
    }

    let indexEncabezado = -1;
    let colDia = 1, colsConcepto = [2], colCargo = 3, colAbono = 4, colReferencia = -1, colImporte = -1;

    // La ventana llega a 30 filas porque BBVA cambió su exportación y ahora
    // antepone 13 renglones de metadatos (titular, cuenta, periodo...) antes de
    // la cabecera real.
    for (let i = 0; i < Math.min(filas.length, 30); i++) {
      const idxsDescripcion = encontrarColumnas(filas[i], ["DESCRIPCION", "DESCRIPCIÓN"]);
      const idxsConceptoKw = encontrarColumnas(filas[i], ["CONCEPTO"]);
      const idxReferenciaKw = encontrarColumna(filas[i], ["REFERENCIA"]);
      // Se unen Descripción y Concepto: según el formato, la referencia que
      // escribe el cliente cae en una o en la otra.
      const idxsTexto = [...idxsDescripcion, ...idxsConceptoKw].sort((a, b) => a - b);
      const idxsHeader = idxsTexto.length ? idxsTexto : idxReferenciaKw !== -1 ? [idxReferenciaKw] : [];
      const idxCargo = encontrarColumna(filas[i], ["CARGO", "RETIRO"]);
      const idxAbono = encontrarColumna(filas[i], ["ABONO", "DEPÓSITO", "DEPOSITO"]);
      const idxImporte = encontrarColumna(filas[i], ["IMPORTE", "MONTO"]);

      if (idxsHeader.length && (idxCargo !== -1 || idxAbono !== -1 || idxImporte !== -1)) {
        indexEncabezado = i;
        colsConcepto = idxsHeader;
        if (idxReferenciaKw !== -1 && !colsConcepto.includes(idxReferenciaKw)) colReferencia = idxReferenciaKw;

        const idxDia = encontrarColumna(filas[i], ["DÍA", "DIA", "FECHA"]);
        if (idxDia !== -1) colDia = idxDia;
        if (idxCargo !== -1 || idxAbono !== -1) {
          if (idxCargo !== -1) colCargo = idxCargo;
          if (idxAbono !== -1) colAbono = idxAbono;
        } else {
          // Formato de una sola columna de Importe con signo (BBVA actual).
          colImporte = idxImporte;
          colCargo = -1;
          colAbono = -1;
        }
        break;
      }
    }

    if (indexEncabezado === -1) {
      message.error(
        "No se reconoció el formato del Excel (no se encontraron las columnas de Cargo/Abono ni de Importe). Verifica que sea el estado de cuenta bancario original.",
      );
      this.setState({ cargando: false });
      return;
    }

    const filasDatos = filas.slice(indexEncabezado + 1);
    const esFilaVacia = (fila) => !fila || fila.every((c) => String(c ?? "").trim() === "");

    const pares = [];
    let cargosIgnorados = 0;

    for (let i = 0; i < filasDatos.length; i++) {
      const fila1 = filasDatos[i];
      if (esFilaVacia(fila1)) continue;
      const diaFila1 = String(fila1[colDia] ?? "").trim();
      if (!diaFila1) continue; // no es inicio de un movimiento, es continuación huérfana

      // Buscar la siguiente fila no vacía.
      let j = i + 1;
      while (j < filasDatos.length && esFilaVacia(filasDatos[j])) j++;
      const fila2 = j < filasDatos.length ? filasDatos[j] : null;
      const diaFila2 = fila2 ? String(fila2[colDia] ?? "").trim() : "";

      const esContinuacion = !!fila2 && !diaFila2;

      let cargo, abono;
      if (colImporte !== -1) {
        // Una sola columna con signo: negativo es cargo, positivo es abono.
        const importe = parseMonto(fila1[colImporte]);
        abono = importe > 0 ? importe : 0;
        cargo = importe < 0 ? -importe : 0;
      } else {
        cargo = parseMonto(fila1[colCargo]);
        abono = parseMonto(fila1[colAbono]);
      }

      const filaConcepto = esContinuacion ? fila2 : fila1;
      const textosConcepto = colsConcepto.map((c) => limpiarTexto(filaConcepto[c])).filter(Boolean);
      const textoReferencia = colReferencia !== -1 ? limpiarTexto(filaConcepto[colReferencia]) : "";
      const conceptoReferencia = [textoReferencia, ...textosConcepto].filter(Boolean).join(" — ");

      if (abono <= 0) {
        if (cargo > 0) cargosIgnorados++;
        if (esContinuacion) i = j; // saltar el par completo
        continue;
      }

      pares.push({
        key: `${i}-${esContinuacion ? j : i}`,
        fila: i + indexEncabezado + 2,
        conceptoReferencia,
        abono,
        filaIdx1: i + indexEncabezado + 1,
        filaIdx2: esContinuacion ? j + indexEncabezado + 1 : null,
      });

      if (esContinuacion) i = j; 
    }

    const candidatos = marcarVentasDuplicadas(
      pares.map((par) => {
        const coincidencias = buscarCoincidencias(par.conceptoReferencia, this.state.ventasPendientes);
        if (coincidencias.length === 0) {
          return { ...par, estado: "sin_referencia" };
        }
        if (coincidencias.length > 1) {
          return { ...par, ventasAmbiguas: coincidencias, estado: "ambiguo" };
        }
        const ventaEncontrada = coincidencias[0];
        if (ventaEncontrada.yaPagada) {
          return { ...par, venta: ventaEncontrada, estado: "ya_pagada" };
        }
        const montoCoincide = Math.abs(par.abono - Number(ventaEncontrada.totalPagar)) < 0.01;
        return {
          ...par,
          venta: ventaEncontrada,
          estado: montoCoincide ? "exacto" : "monto_no_coincide",
        };
      }),
    );

    const seleccionadas = {};
    candidatos.forEach((c) => {
      if (c.estado === "exacto") seleccionadas[c.venta._id] = true;
    });

    this.setState({
      candidatos,
      seleccionadas,
      cargosIgnorados,
      filasOriginales: filas,
      formatoExcel: "bancario",
      cargando: false,
    });
  };

  procesarFilasEscolar = (filas) => {
    const filaEncabezado = filas[0] || [];
    const colMatricula = encontrarColumna(filaEncabezado, ["MATRICULA", "MATRÍCULA"]);
    const colReferencia = (() => {
      const candidatas = encontrarColumnas(filaEncabezado, ["REFERENCIA"]).filter(
        (i) => !normalizar(filaEncabezado[i]).includes("COMPROBANTE"),
      );
      if (candidatas.length === 0) return -1;
      const especifica = candidatas.find((i) => normalizar(filaEncabezado[i]).includes("REFERENCIA GENERADA"));
      return especifica !== undefined ? especifica : candidatas[candidatas.length - 1];
    })();
    const colMonto = encontrarColumna(filaEncabezado, ["MONTO"]);
    const colNombre = encontrarColumna(filaEncabezado, ["NOMBRE"]);
    const colFolio = encontrarColumna(filaEncabezado, ["FOLIO"]);

    const filasDatos = filas.slice(1);
    const esFilaVacia = (fila) => !fila || fila.every((c) => String(c ?? "").trim() === "");

    const pares = [];
    for (let i = 0; i < filasDatos.length; i++) {
      const fila = filasDatos[i];
      if (esFilaVacia(fila)) continue;

      const referenciaExcel = colReferencia !== -1 ? String(fila[colReferencia] ?? "").trim() : "";
      if (!referenciaExcel) continue; // sin referencia no hay nada que conciliar en esta fila

      const matriculaExcel = colMatricula !== -1 ? normalizar(fila[colMatricula]) : "";
      const nombreAlumno = colNombre !== -1 ? String(fila[colNombre] ?? "").trim() : "";
      const folioOperacion = colFolio !== -1 ? String(fila[colFolio] ?? "").trim() : "";

      const montoCelda = colMonto !== -1 ? fila[colMonto] : "";
      const tieneMonto = colMonto !== -1 && String(montoCelda ?? "").trim() !== "";
      const monto = tieneMonto ? parseMonto(montoCelda) : 0;

      pares.push({
        key: `escolar-${i}`,
        fila: i + 2,
        conceptoReferencia: [nombreAlumno, referenciaExcel].filter(Boolean).join(" — "),
        matricula: matriculaExcel,
        folioOperacion,
        tieneMonto,
        abono: monto,
        filaIdx1: i + 1,
        filaIdx2: null,
      });
    }

    const candidatos = marcarVentasDuplicadas(
      pares.map((par) => {
        const coincidencias = buscarCoincidencias(par.conceptoReferencia, this.state.ventasPendientes);
        if (coincidencias.length === 0) {
          return { ...par, estado: "sin_referencia" };
        }
        if (coincidencias.length > 1) {
          return { ...par, ventasAmbiguas: coincidencias, estado: "ambiguo" };
        }
        const ventaEncontrada = coincidencias[0];

        const matriculaCoincide =
          !!par.matricula &&
          (ventaEncontrada.paquetesEscolares || []).some((p) => normalizar(p.matricula) === par.matricula);

        if (ventaEncontrada.yaPagada) {
          return { ...par, venta: ventaEncontrada, matriculaCoincide, estado: "ya_pagada" };
        }
        if (!par.tieneMonto) {
          return { ...par, venta: ventaEncontrada, matriculaCoincide, estado: "confirmar_monto" };
        }
        const montoCoincide = Math.abs(par.abono - Number(ventaEncontrada.totalPagar)) < 0.01;
        return {
          ...par,
          venta: ventaEncontrada,
          matriculaCoincide,
          estado: montoCoincide ? "exacto" : "monto_no_coincide",
        };
      }),
    );

    const seleccionadas = {};
    candidatos.forEach((c) => {
      if (c.estado === "exacto") seleccionadas[c.venta._id] = true;
    });

    this.setState({
      candidatos,
      seleccionadas,
      cargosIgnorados: 0,
      filasOriginales: filas,
      formatoExcel: "escolar",
      cargando: false,
    });
  };

  toggleSeleccion = (ventaId, checked) => {
    this.setState((prev) => ({
      seleccionadas: { ...prev.seleccionadas, [ventaId]: checked },
    }));
  };

  aplicarPagos = () => {
    const { empresaId, headersToken } = this.props;
    const { candidatos, seleccionadas } = this.state;

    const pagos = candidatos
      .filter((c) => (c.estado === "exacto" || c.estado === "confirmar_monto") && seleccionadas[c.venta._id])
      .map((c) => ({
        ventaId: c.venta._id,
        referencia: c.venta.referencia,
        monto: c.estado === "confirmar_monto" ? c.venta.totalPagar : c.abono,
      }));

    if (pagos.length === 0) {
      message.info("No hay pagos seleccionados");
      return;
    }

    this.setState({ cargando: true });
    axios
      .post("ventasEcommerce/aplicarPagosMasivoTransferencia", { empresaId, pagos }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ resultados: res.data.resultados });
        } else {
          message.error(res.data.message || "No se pudieron aplicar los pagos");
        }
      })
      .catch(() => message.error("Error de conexión con el servidor"))
      .finally(() => this.setState({ cargando: false }));
  };

  cerrarYRefrescar = () => {
    this.props.onSuccess();
  };

  colorParaCandidato = (candidato) => {
    const { resultados } = this.state;
    if (candidato.estado === "ambiguo") return COLOR_MONTO_PROBLEMA;
    if (candidato.estado === "ya_pagada") return COLOR_YA_PAGADA;
    if (candidato.estado === "monto_no_coincide") return COLOR_MONTO_PROBLEMA;
    if (candidato.estado === "exacto" || candidato.estado === "confirmar_monto") {
      const resultado = resultados?.find((r) => r.ventaId === candidato.venta._id);
      if (!resultado) {
        return candidato.estado === "confirmar_monto" ? COLOR_MONTO_PROBLEMA : null;
      }
      if (resultado.alreadyApplied) return COLOR_YA_PAGADA;
      if (resultado.success) return COLOR_PAGADA_AHORA;
      return COLOR_MONTO_PROBLEMA; // falló al aplicar en el último momento
    }
    return null; // sin_referencia
  };

  descargarExcelColoreado = () => {
    const { candidatos, filasOriginales } = this.state;

    const colorPorFila = new Map();
    candidatos.forEach((candidato) => {
      const color = this.colorParaCandidato(candidato);
      if (!color) return;
      colorPorFila.set(candidato.filaIdx1, color);
      if (candidato.filaIdx2 != null) colorPorFila.set(candidato.filaIdx2, color);
    });

    // Algunos estados de cuenta declaran un rango con cientos de filas vacías al
    // final; se recortan para no exportarlas. No afecta los índices coloreados,
    // que se cuentan desde arriba.
    let ultimaConDatos = filasOriginales.length - 1;
    while (
      ultimaConDatos >= 0 &&
      (filasOriginales[ultimaConDatos] || []).every((c) => String(c ?? "").trim() === "")
    ) {
      ultimaConDatos--;
    }

    const filas = filasOriginales.slice(0, ultimaConDatos + 1).map((fila, rowIdx) => {
      const color = colorPorFila.get(rowIdx);
      return fila.map((valor) => {
        const esNumero = typeof valor === "number";
        const celda = { v: valor, t: esNumero ? "n" : "s" };
        if (color) celda.s = { fill: { fgColor: { rgb: color } } };
        return celda;
      });
    });

    const worksheet = XLSXStyle.utils.aoa_to_sheet(filas);
    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Conciliación");
    XLSXStyle.writeFile(workbook, `Conciliacion_Transferencias_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  render() {
    const { visible, onCancel } = this.props;
    const {
      cargando,
      ventasPendientes,
      archivoNombre,
      candidatos,
      seleccionadas,
      resultados,
      cargosIgnorados,
      mostrarNoCoincidencias,
      formatoExcel,
    } = this.state;

    const totalSeleccionados = candidatos.filter(
      (c) => (c.estado === "exacto" || c.estado === "confirmar_monto") && seleccionadas[c.venta._id],
    ).length;
    const hayConfirmarMontoSeleccionados = candidatos.some(
      (c) => c.estado === "confirmar_monto" && seleccionadas[c.venta._id],
    );
    const sinReferencia = candidatos.filter((c) => c.estado === "sin_referencia").length;
    const ambiguos = candidatos.filter((c) => c.estado === "ambiguo").length;
    const candidatosVisibles = mostrarNoCoincidencias
      ? candidatos
      : candidatos.filter((c) => c.estado !== "sin_referencia");
    const ventasEncontradasSet = new Set();
    candidatos.forEach((c) => {
      if (c.venta) ventasEncontradasSet.add(c.venta);
      (c.ventasAmbiguas || []).forEach((v) => ventasEncontradasSet.add(v));
    });
    const pendientesNoEncontradas = ventasPendientes.filter(
      (v) => !v.yaPagada && !ventasEncontradasSet.has(v),
    );

    const columnasPendientesNoEncontradas = [
      { title: "Referencia", dataIndex: "referencia", width: 110 },
      { title: "Folio", dataIndex: "serieNumero", width: 110 },
      {
        title: "Cliente",
        render: (_, v) => `${v.clienteOnline?.nombre || ""} ${v.clienteOnline?.apellidoPaterno || ""}`.trim() || "—",
      },
      {
        title: "Total a pagar",
        width: 110,
        render: (_, v) => formatMonto(v.totalPagar),
      },
    ];

    const columnas = [
      { title: "Fila", dataIndex: "fila", width: 60 },
      {
        title: "Concepto / Referencia",
        dataIndex: "conceptoReferencia",
        ellipsis: true,
      },
      ...(formatoExcel === "escolar"
        ? [
            {
              title: "Matrícula",
              width: 90,
              render: (_, r) =>
                r.venta ? (
                  <Tag color={r.matriculaCoincide ? "green" : "warning"}>
                    {r.matriculaCoincide ? "✓ coincide" : "⚠ revisar"}
                  </Tag>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
          ]
        : []),
      {
        title: "Abono",
        dataIndex: "abono",
        width: 110,
        render: (v, r) => (r.estado === "confirmar_monto" ? <Text type="secondary">—</Text> : formatMonto(v)),
      },
      {
        title: "Venta encontrada",
        width: 200,
        render: (_, r) =>
          r.ventasAmbiguas ? (
            <Text type="warning" style={{ fontSize: 11 }}>
              {r.ventasAmbiguas.map((v) => v.serieNumero).join(", ")}
            </Text>
          ) : r.venta ? (
            <span>
              <Text strong>{r.venta.serieNumero}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {r.venta.clienteOnline?.nombre} {r.venta.clienteOnline?.apellidoPaterno}
              </Text>
            </span>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Total a pagar",
        width: 100,
        render: (_, r) => (r.venta ? formatMonto(r.venta.totalPagar) : "—"),
      },
      {
        title: "Estado",
        width: 170,
        render: (_, r) => {
          if (r.estado === "exacto") return <Tag color="green">Coincide exacto</Tag>;
          if (r.estado === "ambiguo") return <Tag color="orange">Coincidencia ambigua — revisar</Tag>;
          if (r.estado === "confirmar_monto") return <Tag color="gold">Falta confirmar monto</Tag>;
          if (r.estado === "ya_pagada") return <Tag color="blue">Pago registrado</Tag>;
          if (r.estado === "monto_no_coincide") return <Tag color="orange">Monto no coincide</Tag>;
          return <Tag>Sin referencia encontrada</Tag>;
        },
      },
      {
        title: "Aplicar",
        width: 70,
        render: (_, r) =>
          r.estado === "exacto" || r.estado === "confirmar_monto" ? (
            <Checkbox
              checked={!!seleccionadas[r.venta._id]}
              onChange={(e) => this.toggleSeleccion(r.venta._id, e.target.checked)}
            />
          ) : null,
      },
    ];

    return (
      <ModalItem
        title={
          <span>
            <FileExcelOutlined /> Conciliar Transferencias por Excel
          </span>
        }
        visible={visible}
        onCancel={onCancel}
        width={1000}
        spinning={cargando}
      >
        {resultados ? (
          <Result
            status="success"
            title="Conciliación aplicada"
            subTitle={`${resultados.filter((r) => r.success && !r.alreadyApplied).length} pago(s) aplicado(s).`}
            extra={[
              <ButtonItem key="descargar" onClick={this.descargarExcelColoreado}>
                Descargar Excel coloreado
              </ButtonItem>,
              <ButtonItem key="cerrar" type="primary" onClick={this.cerrarYRefrescar}>
                Cerrar
              </ButtonItem>,
            ]}
          >
            <div style={{ textAlign: "left", maxHeight: 300, overflowY: "auto" }}>
              {resultados.map((r, idx) => (
                <div key={idx} style={{ marginBottom: 6 }}>
                  {r.success ? (
                    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#f5222d", marginRight: 6 }} />
                  )}
                  <Text strong>{r.serieNumero || r.ventaId}</Text>: {r.message}
                </div>
              ))}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              El Excel descargado marca en verde los movimientos pagados ahora, en azul los que ya
              estaban pagados, y en amarillo los que encontraron referencia pero con un monto distinto
              o sin confirmar.
            </Text>
          </Result>
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 15, marginBottom: 15 }}
              message="Sube el Excel (estado de cuenta bancario, o el de matrícula/referencia capturado manualmente)"
              description="Si es un estado de cuenta, se busca la referencia de cada venta pendiente en el concepto de cada movimiento y solo se marcan como pagadas las que coincidan en monto exacto contra un Abono/Depósito (o un Importe positivo, si el banco usa una sola columna con signo)."
            />
            <Upload
              showUploadList={false}
              customRequest={this.onSubirArchivo}
              accept=".xlsx,.xls"
              disabled={cargando}
            >
              <ButtonItem icon={<UploadOutlined />} disabled={cargando}>
                Subir Excel
              </ButtonItem>
            </Upload>
            {archivoNombre && (
              <Text type="secondary" style={{ marginLeft: 10 }}>
                {archivoNombre}
              </Text>
            )}

            {cargosIgnorados > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 15 }}
                message={`${cargosIgnorados} movimiento(s) de Cargo/Retiro ignorados (no aplican, solo se consideran Abonos/Depósitos).`}
              />
            )}

            {ambiguos > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 15 }}
                message={`${ambiguos} movimiento(s) con coincidencia ambigua`}
                description="La referencia casó con más de una venta, o una misma venta casó con más de un movimiento. No se seleccionan automáticamente: revísalos y aplícalos desde la venta."
              />
            )}

            {candidatos.length > 0 && (
              <>
                <Divider style={{ margin: "16px 0" }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text type="secondary">
                    Mostrando {candidatosVisibles.length} de {candidatos.length} movimiento(s)
                  </Text>
                  <span>
                    <Switch
                      size="small"
                      checked={mostrarNoCoincidencias}
                      onChange={(checked) => this.setState({ mostrarNoCoincidencias: checked })}
                      style={{ marginRight: 8 }}
                    />
                    <Text type="secondary">
                      Ver también los que no encontraron referencia ({sinReferencia})
                    </Text>
                  </span>
                </div>
                <Table
                  size="small"
                  columns={columnas}
                  dataSource={candidatosVisibles}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: "max-content" }}
                />

                {pendientesNoEncontradas.length > 0 && (
                  <Collapse ghost size="small" style={{ marginTop: 15 }}>
                    <Collapse.Panel
                      header={`Ventas pendientes que no se encontraron en el Excel (${pendientesNoEncontradas.length})`}
                      key="pendientesNoEncontradas"
                    >
                      <Table
                        size="small"
                        columns={columnasPendientesNoEncontradas}
                        dataSource={pendientesNoEncontradas}
                        rowKey="_id"
                        pagination={{ pageSize: 8 }}
                        scroll={{ x: "max-content" }}
                      />
                    </Collapse.Panel>
                  </Collapse>
                )}

                {hayConfirmarMontoSeleccionados && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 15 }}
                    message="Hay pagos seleccionados sin monto confirmado en el Excel"
                    description="Se van a aplicar usando el total esperado de la venta. Es responsabilidad de quien acepta haber confirmado por fuera que el monto depositado es correcto."
                  />
                )}

                <div style={{ textAlign: "right", marginTop: 15 }}>
                  {hayConfirmarMontoSeleccionados ? (
                    <Popconfirm
                      title="Confirmas bajo tu responsabilidad que el monto de los pagos sin confirmar es correcto?"
                      okText="Sí, aplicar"
                      cancelText="Cancelar"
                      onConfirm={this.aplicarPagos}
                    >
                      <ButtonItem type="primary" verde disabled={totalSeleccionados === 0}>
                        Aplicar {totalSeleccionados} pago(s) seleccionado(s)
                      </ButtonItem>
                    </Popconfirm>
                  ) : (
                    <ButtonItem
                      type="primary"
                      verde
                      disabled={totalSeleccionados === 0}
                      onClick={this.aplicarPagos}
                    >
                      Aplicar {totalSeleccionados} pago(s) seleccionado(s)
                    </ButtonItem>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalConciliarTransferenciasExcel);
